import { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { analyticsService } from './utils/analytics';
import { captureAttribution } from './utils/utm';
import RouteAnalytics from './components/RouteAnalytics';
import { BookingProvider } from './components/BookingProvider';
import { ApolloProvider } from '@apollo/client';
import { apolloClient } from './apollo/client';
import { Homepage } from './pages/Homepage';
import { Pricing } from './pages/Pricing';
import { Hardware } from './pages/Hardware';
import { About } from './pages/About';
import { Docs } from './pages/Docs';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import { Guide } from './pages/Guide';
import { HowItWorks } from './pages/HowItWorks';
import { Contact } from './pages/Contact';
import { Book } from './pages/Book';
import { MobileApp } from './pages/MobileApp';
import Privacy from './pages/Privacy';
import AdConceptPreview from './pages/AdConceptPreview';
import FacebookMerchantLaunchConsole from './pages/FacebookMerchantLaunchConsole';
import FacebookMerchantLaunchPackage from './pages/FacebookMerchantLaunchPackage';
import CreativeExport from './pages/CreativeExport';
import LoginRedirect from './components/LoginRedirect';
import SalesCodeLanding from './pages/SalesCodeLanding';
import BatchVoucherLanding from './pages/BatchVoucherLanding';
import CampaignLanding from './pages/CampaignLanding';
import NotFound from './pages/NotFound';
import {
  PendingVoucherBanner,
  getPendingVoucher,
  isPendingVoucherDismissed,
  dismissPendingVoucher,
} from '@coupling/ui';

// Capture marketing attribution from URL (utm_*, gclid, fbclid) before any
// analytics/conversion event fires, so the captured context is available to
// downstream conversion calls.
captureAttribution();

// Initialize Firebase Analytics + Meta Pixel (each is a no-op if its
// respective env config is not provided).
analyticsService.initialize();

// Capture UTM parameters and attribution data on page load
captureAttribution();

function AppContent() {
  const location = useLocation();
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    document.dispatchEvent(new Event('prerender-ready'));
  }, []);

  const voucher = getPendingVoucher();
  const dismissed = voucher ? isPendingVoucherDismissed(voucher.shareCode) : true;
  const isClaimPage = location.pathname.startsWith('/claim/');

  const handleDismiss = () => {
    if (voucher) {
      dismissPendingVoucher(voucher.shareCode);
      forceUpdate((n) => n + 1);
    }
  };

  return (
    <>
      <RouteAnalytics />
      <Routes>
        <Route path="/" element={<Homepage />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/hardware" element={<Hardware />} />
        <Route path="/about" element={<About />} />
        <Route path="/docs" element={<Docs />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/guide" element={<Guide />} />
        <Route path="/app" element={<MobileApp />} />
        <Route path="/preview/facebook-merchant-acquisition" element={<AdConceptPreview />} />
        <Route path="/preview/facebook-merchant-launch-console" element={<FacebookMerchantLaunchConsole />} />
        <Route path="/preview/facebook-merchant-launch-package" element={<FacebookMerchantLaunchPackage />} />
        <Route path="/preview/creative-export" element={<CreativeExport />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/book" element={<Book />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/login" element={<LoginRedirect />} />
        <Route path="/signup" element={<LoginRedirect />} />
        <Route path="/s/:code" element={<SalesCodeLanding />} />
        <Route path="/claim/:code" element={<><Homepage /><BatchVoucherLanding /></>} />
        <Route path="/lp/:slug" element={<CampaignLanding />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      {voucher && !dismissed && !isClaimPage && (
        <PendingVoucherBanner
          merchantName={voucher.merchantName}
          displayAmount={voucher.displayAmount}
          claimUrl={voucher.claimUrl}
          onDismiss={handleDismiss}
        />
      )}
    </>
  );
}

function App() {
  return (
    <ApolloProvider client={apolloClient}>
      <Router>
        <BookingProvider>
          <AppContent />
        </BookingProvider>
      </Router>
    </ApolloProvider>
  );
}

export default App;
